// hooks/useViewItemList.js
import { useEffect, useRef } from 'react';
import { trackViewItemList } from '../lib/gtm';

export const useViewItemList = (playlist, listId, listName, category, category2) => {
  const railRef = useRef(null);
  const firedRef = useRef(false);

  // Always-current refs — updated synchronously on every render, no re-render side effects.
  // This lets observer callbacks and the playlist-arrival effect always read the latest
  // values without needing them in a dependency array (which would recreate the observer).
  const playlistRef = useRef(playlist);
  const configRef = useRef({ listId, listName, category, category2 });
  const isIntersectingRef = useRef(false);

  playlistRef.current = playlist;
  configRef.current = { listId, listName, category, category2 };

  // ─── Phase 1: Intersection observer (mounted once, never recreated) ────────
  // Tracks whether the rail is in the viewport and attempts to fire the event.
  // Using an empty dep array avoids teardown/recreate cycles that were causing
  // stale-closure captures of an empty playlist in staging.
  useEffect(() => {
    if (!railRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isIntersectingRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          maybeFireEvent();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(railRef.current);

    return () => observer.disconnect();

    function maybeFireEvent() {
      if (firedRef.current || !playlistRef.current.length) return;
      firedRef.current = true;
      const { listId, listName, category, category2 } = configRef.current;
      trackViewItemList(playlistRef.current, listId, listName, category, category2);
      observer.disconnect();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Phase 2: Playlist-arrival guard ──────────────────────────────────────
  // Handles the case where the element is already in the viewport when the
  // playlist finishes loading (observer already fired but playlist was empty).
  // Reads isIntersectingRef so it only fires if the rail is actually visible.
  useEffect(() => {
    if (firedRef.current || !playlist.length || !isIntersectingRef.current) return;
    firedRef.current = true;
    trackViewItemList(playlist, listId, listName, category, category2);
  }, [playlist, listId, listName, category, category2]);

  return railRef; // attach to the rail wrapper div
};
